import './App.css';
import AppMainLayout from "./app/views/layout/AppMainLayout";
import {BrowserRouter, Navigate, Route, Routes} from "react-router-dom";
import {useEffect} from "react";
import CalendarView from "./app/views/calendar/CalendarView";
import ErrorPage from "./app/views/ErrorPage";
import PopupsContainer from "./app/views/popups/PopupsContainer";
import ReportTimeView from "./app/views/reports/ReportTimeView";
import ReportNegativeRatings from "./app/views/reports/ReportNegativeRatingsView";
import ReportTimeDuplicatesView from "./app/views/reports/ReportTimeDuplicatesView";
import ReportPayRateChangeView from "./app/views/reports/ReportPayRateChangeView";
import EventCostsReport from "./app/views/reports/EventCostsReport";
import {useAppDispatch, useAppSelector} from "./app/hooks";
import {selectCurrentEmployee, selectDepartment, setDepartment} from "./features/AppStateSlice";
import MHomePage from "./app/mobile/pages/MHomePage";
import {useMediaQuery} from "react-responsive";
import MAppMainLayout from "./app/mobile/layout/MAppMainLayout";
import {Container} from "react-bootstrap";
import LogisticsView from "./app/views/logistics/LogisticsView";
import LogisticsTimeReportView from "./app/views/logistics/reports/time-report/LogisticsTimeReportView";
import LogisticsWorkReportView from "./app/views/logistics/reports/transport-work-report/LogisticsWorkReportView";
import FuelInvoicesView from "./app/views/fuel-invoices/FuelInvoicesView";
import RoadBooksView from "./app/views/reports/RoadBooksView";
import LogisticsScheduleReportView from "./app/views/logistics/reports/schedule-report/LogisticsScheduleReportView";
import LogisticsDriverChangeLogReportView from "./app/views/logistics/reports/driver-change-log/LogisticsDriverChangeLogReportView";
import LogisticsHoursDiscrepancyReportView from "./app/views/logistics/reports/hours-discrepancies/LogisticsHoursDiscrepancyReportView";
import CalendarChangeLogReportView from "./app/views/calendar/change-log/CalendarChangeLogReportView";

function LogisticsScheduleRoute() {
    const dispatch = useAppDispatch();
    const currentEmployee = useAppSelector(selectCurrentEmployee);
    const department = useAppSelector(selectDepartment);

    useEffect(() => {
        if (currentEmployee?.isLogistics && currentEmployee.canSeeLogisticsScheduleReport && department !== "logistics") {
            dispatch(setDepartment("logistics"));
        }
    }, [currentEmployee?.isLogistics, currentEmployee?.canSeeLogisticsScheduleReport, department, dispatch]);

    if (!currentEmployee) {
        return null;
    }

    if (!currentEmployee.isLogistics || !currentEmployee.canSeeLogisticsScheduleReport) {
        return <Navigate to={"/"} replace/>;
    }

    return <LogisticsScheduleReportView/>;
}

function LogisticsDriverChangeLogRoute() {
    const currentEmployee = useAppSelector(selectCurrentEmployee);

    if (!currentEmployee) {
        return null;
    }

    if (!currentEmployee.isAdmin) {
        return <Navigate to={"/"} replace/>;
    }

    return <LogisticsDriverChangeLogReportView/>;
}

function LogisticsHoursDiscrepancyReportRoute() {
    const dispatch = useAppDispatch();
    const currentEmployee = useAppSelector(selectCurrentEmployee);
    const department = useAppSelector(selectDepartment);

    useEffect(() => {
        if (currentEmployee?.isAdmin && currentEmployee.isLogistics && department !== "logistics") {
            dispatch(setDepartment("logistics"));
        }
    }, [currentEmployee?.isAdmin, currentEmployee?.isLogistics, department, dispatch]);

    if (!currentEmployee) {
        return null;
    }

    if (!currentEmployee.isAdmin || !currentEmployee.isLogistics) {
        return <Navigate to={"/"} replace/>;
    }

    return <LogisticsHoursDiscrepancyReportView/>;
}

function CalendarChangeLogRoute() {
    const currentEmployee = useAppSelector(selectCurrentEmployee);
    const department = useAppSelector(selectDepartment);

    if (!currentEmployee) return null;
    if (!currentEmployee.isAdmin || department === "logistics" || !department) {
        return <Navigate to={"/"} replace/>;
    }
    return <CalendarChangeLogReportView/>;
}

function App() {
    const currentEmployee = useAppSelector(selectCurrentEmployee);
    let isMobile = useMediaQuery({query: `(max-width: 500px)`});
    const canUseMobileApp = currentEmployee?.canUseMobileApp;
    const canUseWebApp = currentEmployee?.canUseWebApp;

    const department = useAppSelector(selectDepartment);

    if (!canUseMobileApp && !canUseWebApp) {
        return <Container>
            <h3>Нямате права за достъп до това приложение.</h3>
        </Container>
    }

    return (
        <BrowserRouter>
            {
                ((isMobile && canUseMobileApp) || !canUseWebApp) ? <MAppMainLayout>
                        <Routes>
                            <Route path={"/"} element={<MHomePage/>} errorElement={<ErrorPage/>}/>
                        </Routes>
                        <PopupsContainer/>
                    </MAppMainLayout>
                    :
                    <AppMainLayout>
                        <Routes>
                            <Route path={"/"}
                                   element={department !== "logistics" ? <CalendarView/> : <LogisticsView/>}
                                   errorElement={<ErrorPage/>}
                            />
                            <Route path={"/transport/fuel-invoices"}
                                   element={<FuelInvoicesView/>}
                                   errorElement={<ErrorPage/>}
                            />
                            <Route path={"/reports/schedule"} element={<LogisticsScheduleRoute/>}
                                   errorElement={<ErrorPage/>}/>
                            <Route path={"/reports/driver-change-log"} element={<LogisticsDriverChangeLogRoute/>}
                                   errorElement={<ErrorPage/>}/>
                            <Route path={"/reports/calendar-change-log"} element={<CalendarChangeLogRoute/>}
                                   errorElement={<ErrorPage/>}/>

                            {
                                currentEmployee?.isAdmin && <>
                                    {
                                        department !== "logistics" ?
                                            <Route path={"/reports/time"} element={<ReportTimeView/>}
                                                   errorElement={<ErrorPage/>}/>
                                            :
                                            <Route path={"/reports/time"} element={<LogisticsWorkReportView/>}
                                                   errorElement={<ErrorPage/>}/>
                                    }
                                    {
                                        department === "logistics" &&
                                        <Route path={"/reports/km-hours"} element={<LogisticsTimeReportView/>}
                                               errorElement={<ErrorPage/>}/>
                                    }
                                    <Route path={"/reports/hours-discrepancies"} element={<LogisticsHoursDiscrepancyReportRoute/>}
                                           errorElement={<ErrorPage/>}/>
                                    <Route path={"/reports/negative-rating"} element={<ReportNegativeRatings/>}
                                           errorElement={<ErrorPage/>}/>
                                    <Route path={"/reports/timeduplicates"} element={<ReportTimeDuplicatesView/>}
                                           errorElement={<ErrorPage/>}/>
                                    {
                                        (department === "logistics" && (currentEmployee?.isSuperAdmin || (currentEmployee?.isAdmin && currentEmployee?.canSeePayment))) &&
                                        <Route path={"/reports/event-costs"} element={<EventCostsReport/>} errorElement={<ErrorPage/>}/>
                                    }
                                    {
                                        // Road books report - visible for logistics department only
                                        department === "logistics" &&
                                        <Route path={"/reports/roadbooks"} element={<RoadBooksView/>} errorElement={<ErrorPage/>}/>
                                    }
                                    <Route path={"/reports/payratechanges"} element={<ReportPayRateChangeView/>}
                                           errorElement={<ErrorPage/>}/>
                                </>
                            }
                        </Routes>
                        <PopupsContainer/>
                    </AppMainLayout>
            }
        </BrowserRouter>);
}

export default App;
